import { uploadFilePromise } from '@/utils/aliyunUpload'
import { localUpload, type LocalUploadImageState } from '@/components/ImageSelect/service'
import { getLocalImageSize } from '@/utils'

// ── 类型定义 ──────────────────────────────────────────────

/**
 * 资产上传来源类型（对齐后端 AssetSourceEnum 枚举值 0-11）
 *
 * 0=系统       1=生图               2=本地上传        3=姿势图
 * 4=蒙版图     5=3D截图             6=模型发布创作记录  7=视频
 * 8=PSD        9=模型训练参考图      10=自由画布-图片    11=自由画布-视频
 *
 * @example
 *   import { UploadType, UPLOAD_SOURCE_TYPE } from '@/hooks/useAssetUpload'
 *   const type: UploadType = UPLOAD_SOURCE_TYPE.CANVAS_VIDEO // 11
 */
export type UploadType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11

/**
 * localUpload API 实际接受的 type 值（后端接口约束）
 * safeRegister 内部调用 localUpload 时通过类型断言桥接 UploadType → LocalUploadApiType
 */
type LocalUploadApiType = 2 | 3 | 4 | 5

/**
 * 资产上传来源类型常量（对齐后端 AssetSourceEnum）
 *
 * 调用方使用示例：
 *   registerUploadedAsset(file, url, UPLOAD_SOURCE_TYPE.CANVAS)
 *   registerUploadedAsset(file, url, UPLOAD_SOURCE_TYPE.CANVAS_VIDEO)
 */
export const UPLOAD_SOURCE_TYPE = {
  /** 系统 */
  SYSTEM: 0,
  /** 生图 */
  GENERATE: 1,
  /** 普通图库上传（默认，会展示在图库列表中） */
  GALLERY: 2,
  /** 姿势图上传 */
  POSE: 3,
  /** 蒙版/对比图上传 */
  MASK: 4,
  /** 参考图上传（3D截图） */
  REFERENCE: 5,
  /** 模型发布创作记录上传 */
  MODEL_PUBLISH: 6,
  /** 视频 */
  VIDEO: 7,
  /** PSD */
  PSD: 8,
  /** 模型训练素材（不展示在图库中） */
  MODEL_TRAIN: 9,
  /** 自由画布-图片（不展示在AI创作图库中） */
  CANVAS: 10,
  /** 自由画布-视频（不展示在AI创作图库中） */
  CANVAS_VIDEO: 11,
} as const satisfies Record<string, UploadType>

/** 从 UPLOAD_SOURCE_TYPE 常量推导出的值类型 */
export type UploadSourceTypeValue = (typeof UPLOAD_SOURCE_TYPE)[keyof typeof UPLOAD_SOURCE_TYPE]

// ── 接口定义 ──────────────────────────────────────────────

export interface AssetUploadOptions {
  type: UploadType
  artworkId?: number
  checkSpace?: boolean
  onProgress?: (progress: { percent: number }) => void
}

export interface AssetUploadResult {
  ossUrl: string
  record: LocalUploadImageState | null
}

export interface RegisterAssetParams {
  url: string
  width: number
  height: number
  size: number
  type: UploadType
  artworkId?: number
}

// TODO: 第二期实现空间校验逻辑，阻断上传 + 弹窗引导付费
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function checkSpaceBeforeUpload(_fileSize: number): Promise<boolean> {
  return true
}

/**
 * 单文件上传到 OSS 并注册后端索引
 */
export async function uploadAndRegister(
  file: File,
  options: AssetUploadOptions
): Promise<AssetUploadResult> {
  if (options.checkSpace) {
    const canUpload = await checkSpaceBeforeUpload(file.size)
    if (!canUpload) {
      throw new Error('空间不足')
    }
  }

  const ossUrl = await uploadFilePromise(file, {
    onProgress: options.onProgress
      ? (d: { percent: number }) => options.onProgress!(d)
      : undefined,
  })

  const [imageSize] = await getLocalImageSize([file])

  const record = await safeRegister({
    url: ossUrl,
    width: imageSize.width,
    height: imageSize.height,
    size: file.size,
    type: options.type,
    artworkId: options.artworkId,
  })

  return { ossUrl, record }
}

/**
 * 批量上传并注册
 */
export async function batchUploadAndRegister(
  files: File[],
  options: AssetUploadOptions
): Promise<AssetUploadResult[]> {
  return Promise.all(files.map((file) => uploadAndRegister(file, options)))
}

/**
 * 仅注册已上传到 OSS 的资源（不重复上传）
 * 重载1：直接传入参数对象（需自行提供 width/height）
 */
export async function registerUploadedAsset(
  params: RegisterAssetParams
): Promise<LocalUploadImageState | null>
/**
 * 仅注册已上传到 OSS 的资源（不重复上传）
 * 重载2：传入 File 对象，自动获取图片尺寸
 * @param file 本地文件对象
 * @param url 已上传的 OSS URL
 * @param type 上传类型
 * @param artworkId 可选的作品ID
 */
export async function registerUploadedAsset(
  file: File,
  url: string,
  type: UploadType,
  artworkId?: number
): Promise<LocalUploadImageState | null>
export async function registerUploadedAsset(
  paramsOrFile: RegisterAssetParams | File,
  url?: string,
  type?: UploadType,
  artworkId?: number
): Promise<LocalUploadImageState | null> {
  // 重载2：传入 File 对象
  if (paramsOrFile instanceof File) {
    const file = paramsOrFile
    if (!url || !type) {
      throw new Error('[registerUploadedAsset] 使用 File 重载时，url 和 type 参数必填')
    }
    const size = await getLocalImageSize(file)
    return safeRegister({
      url,
      width: size.width,
      height: size.height,
      size: file.size,
      type,
      artworkId,
    })
  }
  // 重载1：传入参数对象
  return safeRegister(paramsOrFile)
}

/**
 * 安全注册：localUpload 失败时降级返回 null，不阻断上传结果
 */
async function safeRegister(params: RegisterAssetParams): Promise<LocalUploadImageState | null> {
  try {
    const { data } = await localUpload({
      type: params.type as LocalUploadApiType,
      artworkId: params.artworkId,
      image: [
        {
          width: params.width,
          height: params.height,
          image: params.url,
          size: params.size,
        },
      ],
    })
    return data?.[0] ?? null
  } catch (error) {
    console.warn('[useAssetUpload] 索引注册失败，不影响已上传文件', error)
    return null
  }
}
